import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import * as Sentry from '@sentry/react';
// import { AdminHome } from './Admin/home';
import NavigationBar from './components/Navbar/NavigationBar'; // Adjust the path if needed
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Footer } from './components/Footer';
import InstallPrompt from './components/InstallPrompt';

// import { NavBar } from './components/Home/navBar';
// import {NavigationBar} from './components/Navbar/NavigationBar'
// import {Collections} from './components/collections';
// import { CollectionDetails } from './components/CollectionDetail'; 
import './global.css';
import { watchAdminStatus } from './utils/adminAuth';
import { auth } from './config/firebase';
import { setSentryUser } from './sentry';

const Home = React.lazy(() => import('./components/home').then((m) => ({ default: m.Home })));
const Auth = React.lazy(() => import('./components/auth').then((m) => ({ default: m.Auth })));
const AdminHome = React.lazy(() => import('./Admin/AdminHome').then((m) => ({ default: m.AdminHome })));
const Dictionary = React.lazy(() => import('./components/dic').then((m) => ({ default: m.Dictionary })));
const LiteratureList = React.lazy(() =>
  import('./Literature/LiteratureList').then((m) => ({ default: m.LiteratureList }))
);
const SavedWordsPage = React.lazy(() => import('./components/fav').then((m) => ({ default: m.SavedWordsPage })));
const Quiz = React.lazy(() => import('./components/Quiz'));
const PrivacyPolicy = React.lazy(() => import('./components/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./components/TermsOfService'));
const About = React.lazy(() => import('./components/about').then((m) => ({ default: m.About })));
const GrammarList = React.lazy(() => import('./Grammar/GrammarList'));
const UserSuggestion = React.lazy(() =>
  import('./components/userSuggestion').then((m) => ({ default: m.UserSuggestion }))
);
const ContributionFeed = React.lazy(() =>
  import('./components/ContributionFeed').then((m) => ({ default: m.ContributionFeed }))
);
const KumaoniToEnglishPage = React.lazy(() =>
  import('./components/SeoOpportunityPages').then((m) => ({ default: m.KumaoniToEnglishPage }))
);
const KumaoniDaysAndMonthsPage = React.lazy(() =>
  import('./components/SeoOpportunityPages').then((m) => ({ default: m.KumaoniDaysAndMonthsPage }))
);
const KumaoniTranslatorPage = React.lazy(() =>
  import('./components/SeoOpportunityPages').then((m) => ({ default: m.KumaoniTranslatorPage }))
);
const UttarakhandLanguagePage = React.lazy(() =>
  import('./components/SeoOpportunityPages').then((m) => ({ default: m.UttarakhandLanguagePage }))
);
const WordMeaningPage = React.lazy(() => import('./components/WordMeaningPage'));
const DictionaryWordIndex = React.lazy(() => import('./components/DictionaryWordIndex'));
const FolkSongsList = React.lazy(() => import('./components/FolkSongsList'));
const FolkSongDetail = React.lazy(() => import('./components/FolkSongDetail'));
const LyricsSubmissionForm = React.lazy(() => import('./components/LyricsSubmissionForm'));
const AdminLyricsReview = React.lazy(() => import('./Admin/AdminLyricsReview'));
const LearnHome = React.lazy(() => import('./components/Learn/LearnHome'));
const CourseDetail = React.lazy(() => import('./components/Learn/CourseDetail'));
const LessonPlayer = React.lazy(() => import('./components/Learn/LessonPlayer'));
const AdminLearn = React.lazy(() => import('./Admin/AdminLearn'));
const WordOfTheDayPage = React.lazy(() => import('./components/WordOfTheDayPage'));

function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  return null;
}

function RequireAuth({ children }) {
  const [authReady, setAuthReady] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
      setAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  if (!authReady) return null;
  if (!user) {
    return (
      <Navigate
        to="/auth"
        replace
        state={{
          from: location.pathname,
          authNotice: 'Please sign in to access this page.',
        }}
      />
    );
  }
  return children;
}

function RequireAdmin({ children }) {
  const [ready, setReady] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const unsub = watchAdminStatus(auth, ({ isAdmin: next }) => {
      setIsAdmin(next);
      setReady(true);
    });
    return () => unsub();
  }, []);

  if (!ready) return null;
  if (!isAdmin) {
    return (
      <Navigate
        to="/auth"
        replace
        state={{ from: location.pathname, authNotice: 'Admin access required.' }}
      />
    );
  }
  return children;
}

function App() {
  useEffect(() => {
    const observer = new ResizeObserver(() => {});
    observer.observe(document.body);
    return () => observer.disconnect();
  }, []);

  // Keep Sentry's active user context in sync with Firebase auth state.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => setSentryUser(user));
    return () => unsubscribe();
  }, []);
  
  return (
    <div className="App">
      <InstallPrompt />
      {/* Wrap the entire application with BrowserRouter */}
      <Router>
      <AppContent />
      </Router>
    </div>
  );
}

function AppContent() {
  const location = useLocation();
  const hideFooter = location.pathname.startsWith('/songs') || location.pathname.startsWith('/learn') || location.pathname.startsWith('/dictionary') ||location.pathname.startsWith('/admin') || location.pathname.startsWith('/community') || location.pathname.startsWith('/dictionary');
  const hideNavbar = /^\/learn\/[^/]+\/watch\//.test(location.pathname);
  const [trackersReady, setTrackersReady] = useState(false);

  useEffect(() => {
    let timerId;
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(() => setTrackersReady(true), { timeout: 2500 });
      return () => window.cancelIdleCallback(idleId);
    }
    timerId = window.setTimeout(() => setTrackersReady(true), 1800);
    return () => window.clearTimeout(timerId);
  }, []);

  return (
    <>
      <ScrollToTop />
      {trackersReady && <Analytics />}
      {trackersReady && <SpeedInsights />}
        {/* nav bar component */}
        {/* <NavBar /> */}
        {!hideNavbar && <NavigationBar/>}
        <main id="main-content">
        <Sentry.ErrorBoundary
          fallback={({ error, resetError }) => (
            <div style={{ minHeight: '50vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem', padding: '2rem', textAlign: 'center' }}>
              <h2 style={{ color: '#1E3E62', fontFamily: 'Nunito, sans-serif' }}>Something went wrong</h2>
              <p style={{ color: '#64748b', maxWidth: '400px' }}>An unexpected error occurred. Our team has been notified.</p>
              <button
                onClick={resetError}
                style={{ padding: '0.5rem 1.5rem', background: '#3730a3', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}
              >
                Try again
              </button>
            </div>
          )}
        >
        <Suspense fallback={<div style={{ minHeight: '50vh' }} aria-busy="true" />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<Home />} />
          <Route path="/admin" element={<AdminHome />} />
          <Route
            path="/admin/lyrics"
            element={
              <RequireAdmin>
                <AdminLyricsReview standalone />
              </RequireAdmin>
            }
          />
          <Route path="/dictionary" element={<Dictionary />} />
          <Route path="/dictionary/words" element={<DictionaryWordIndex />} />
          <Route path="/dictionary/words/:letter" element={<DictionaryWordIndex />} />
          <Route path="/words" element={<DictionaryWordIndex />} />
          <Route path="/words/:letter" element={<DictionaryWordIndex />} />
          <Route path="/auth" element={<Auth />} />
          <Route
            path="/grammar"
            element={
              <RequireAuth>
                <GrammarList />
              </RequireAuth>
            }
          />
          {/* <Route path="/grammarup" element={<GrammarUpload />} /> */}
          {/* <Route path="/litup" element={<LiteratureUpload/>} /> */}
          <Route
            path="/literature"
            element={
              <RequireAuth>
                <LiteratureList />
              </RequireAuth>
            }
          />
          <Route path="/savedwords" element={<SavedWordsPage/>}  />
          <Route path="/about" element={<About/>}/>
          <Route path="/quiz" element={<Quiz />} />
          <Route path="/songs" element={<FolkSongsList />} />
          <Route path="/songs/:songId" element={<FolkSongDetail />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/contribute" element={<UserSuggestion />} />
          <Route
            path="/contribute-lyrics"
            element={
              <RequireAuth>
                <LyricsSubmissionForm />
              </RequireAuth>
            }
          />
            <Route path="/learn" element={<LearnHome />} />
            <Route path="/learn/:courseId" element={<CourseDetail />} />
            <Route path="/learn/:courseId/watch/:videoId" element={<LessonPlayer />} />
            <Route path="/learn/standalone/watch/:videoId" element={<LessonPlayer />} />
          <Route
            path="/admin/learn"
            element={
              <RequireAdmin>
                <AdminLearn standalone />
              </RequireAdmin>
            }
          />
          <Route path="/kumaoni-to-english" element={<KumaoniToEnglishPage />} />
          <Route path="/kumaoni-translator" element={<KumaoniTranslatorPage />} />
          <Route path="/uttarakhand-language" element={<UttarakhandLanguagePage />} />
          <Route path="/kumaoni-days-and-months" element={<KumaoniDaysAndMonthsPage />} />
          <Route
            path="/dictionary/english-kumouni/:word/:meaningSlug?"
            element={<WordMeaningPage />}
          />
          <Route path="/:postType/:postId" element={<ContributionFeed />} />
          <Route path="/community/:postType/:postId" element={<ContributionFeed />} />
          <Route path="/community" element={<ContributionFeed />} />
          {/* <Route path="/AudioRecorder" element={<AudioRecorder wordId="test2isha" />} /> */}


          {/* <Route path="/collection" element={<Collections />} /> */}
          {/* <Route path="/collections/:collectionName" element={<CollectionDetails />} /> */}
          <Route path="/word-of-the-day/:id" element={<WordOfTheDayPage />} />
        </Routes>
        </Suspense>
        </Sentry.ErrorBoundary>
        </main>
        {/* footer component */}
        {!hideFooter && <Footer />}
    </>
  );
}

export default App;
